"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _Dialog = _interopRequireDefault(require("@mui/material/Dialog"));
var _DialogActions = _interopRequireDefault(require("@mui/material/DialogActions"));
var _DialogContent = _interopRequireDefault(require("@mui/material/DialogContent"));
var _DialogTitle = _interopRequireDefault(require("@mui/material/DialogTitle"));
var _LinearProgress = _interopRequireDefault(require("@mui/material/LinearProgress"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireDefault(require("react"));
var _Button = _interopRequireDefault(require("./Button.js"));
var _Flex = _interopRequireDefault(require("./Flex.js"));
var _String = _interopRequireDefault(require("./String.js"));
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
/**
 * Abstraction of Material UI's `Dialog` component
 * 
 * Original author @heron-web/material (no longer maintained)
 */class Dialog extends _react.default.PureComponent {
  /**
   * Render.
   * 
   * @return {ReactNode}
   */
  render() {
    return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_Dialog.default, {
      className: this.props.className,
      disableEscapeKeyDown: this.disabled || this.props.disableImplicitClose,
      disableScrollLock: !this.props.useScrollLock,
      fullScreen: this.props.fullScreen,
      fullWidth: this.props.fullWidth,
      maxWidth: this.props.width,
      PaperProps: this.paperProps,
      onClose: !this.disabled ? (event, reason) => {
        if (this.disableImplicitClose && reason !== "backdropClick") {
          this.props.onclose(event, reason);
        }
      } : undefined,
      open: this.props.open,
      TransitionComponent: this.props.transition,
      TransitionProps: this.props.transitionProps,
      transitionDuration: this.props.noTransition ? 0 : this.props.transitionDuration,
      children: [(this.props.title || this.props.loading) && this.renderTitle(), this.renderContent(), this.hasActions && this.renderActions()]
    });
  }

  /**
   * Render our actions.
   * 
   * @return {ReactNode}
   */
  renderActions() {
    return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_DialogActions.default, {
      className: this.props.classNameActions,
      children: [this.props.actions, this.renderDefaultActions()]
    });
  }

  /**
   * Render the content.
   * 
   * @return {ReactNode}
   */
  renderContent() {
    const children = this.props.children ? this.renderChildren() : this.renderText();
    if (!this.props.noContent) {
      return /*#__PURE__*/(0, _jsxRuntime.jsx)(_DialogContent.default, {
        className: this.props.classNameContent,
        dividers: this.props.contentDividers,
        children: children
      });
    } else return children;
  }

  /**
   * Render our children.
   *
   * @return {ReactNode}
   */
  renderChildren() {
    if (!this.props.noFlex) {
      return /*#__PURE__*/(0, _jsxRuntime.jsx)(_Flex.default, {
        gap: this.props.gap,
        mb: this.effectiveContentPaddingBottom,
        ...this.props.FlexProps,
        children: this.props.children
      });
    } else return this.props.children;
  }

  /**
   * Render default actions.
   * 
   * @return {ReactNode}
   */
  renderDefaultActions() {
    const onOk = this.props.onOk;
    const okDisabled = this.props.okDisabled;
    const hasOk = onOk && !this.props.noOkButton;
    const hasClose = !this.props.noCloseButton;
    const closeBtn = hasClose && /*#__PURE__*/(0, _jsxRuntime.jsx)(_Button.default, {
      disabled: this.disabled,
      label: this.props.closeLabel || (!hasOk ? "OK" : "Cancel"),
      onClick: this.props.onClose,
      variant: "text"
    });
    return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_Flex.default, {
      alignItems: "center",
      columnar: true,
      hidden: !hasClose && !hasOk,
      children: [!this.props.invertActionButtons && closeBtn, hasOk && /*#__PURE__*/(0, _jsxRuntime.jsx)(_Button.default, {
        disabled: this.disabled || okDisabled,
        label: this.props.okLabel,
        onClick: onOk,
        variant: "contained"
      }), this.props.invertActionButtons && closeBtn]
    });
  }

  /**
   * Render our text.
   * 
   * @return {ReactNode}
   */
  renderText() {
    return /*#__PURE__*/(0, _jsxRuntime.jsx)(_String.default, {
      color: "textSecondary",
      str: this.props.str,
      ...this.stringProps
    });
  }

  /**
   * Render the title.
   *
   * @return {ReactNode}
   */
  renderTitle() {
    return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_Flex.default, {
      gap: 0,
      children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_DialogTitle.default, {
        className: this.props.classNameTitle,
        children: this.props.title
      }), this.props.loading && /*#__PURE__*/(0, _jsxRuntime.jsx)(_LinearProgress.default, {})]
    });
  }

  /**
   * Get whether the controls should be disabled.
   * 
   * @return {Boolean}
   */
  get disabled() {
    return this.props.disabled || this.props.loading;
  }

  /**
   * Get the effective padding bottom multiplier to apply to 
   * our content container as the appropriate amount varies 
   * depending on the components we're using.
   * 
   * @return {Number}
   */
  get effectiveContentPaddingBottom() {
    return !this.props.contentDividers && !this.props.actions && this.props.noCloseButton && (!this.props.onOk || this.props.noOkButton) ? this.props.contentPaddingBottom * (!this.props.title ? 0.75 : 1) : 0;
  }

  /**
   * Get whether we have actions to render.
   *
   * @return {ReactNode}
   */
  get hasActions() {
    return !!(this.props.actions || !this.props.noCloseButton || this.props.onOk && !this.props.noOkButton);
  }

  /**
   * `PaperProps`
   *
   * @return {Object}
   */
  get paperProps() {
    return {
      className: this.props.classNamePaper,
      elevation: this.props.elevation,
      square: this.props.square,
      variant: this.props.variant,
      ...this.props.PaperProps
    };
  }

  /**
   * Props to apply to our `String`
   * 
   * @return {Object}
   */
  get stringProps() {
    return {
      FlexProps: {
        mb: this.effectiveContentPaddingBottom,
        ...this.props.StringProps?.FlexProps
      },
      ...this.props.StringProps
    };
  }
  static propTypes = {
    FlexProps: _propTypes.default.object,
    PaperProps: _propTypes.default.object,
    StringProps: _propTypes.default.object,
    actions: _propTypes.default.node,
    children: _propTypes.default.node,
    className: _propTypes.default.string,
    classNameActions: _propTypes.default.string,
    classNameContent: _propTypes.default.string,
    classNamePaper: _propTypes.default.string,
    classNameTitle: _propTypes.default.string,
    closeLabel: _propTypes.default.string,
    contentDividers: _propTypes.default.bool,
    contentPaddingBottom: _propTypes.default.number,
    disabled: _propTypes.default.bool,
    disableImplicitClose: _propTypes.default.bool,
    elevation: _propTypes.default.number,
    fullScreen: _propTypes.default.bool,
    fullWidth: _propTypes.default.bool,
    gap: _propTypes.default.number,
    invertActionButtons: _propTypes.default.bool,
    loading: _propTypes.default.bool,
    noContent: _propTypes.default.bool,
    noFlex: _propTypes.default.bool,
    noCloseButton: _propTypes.default.bool,
    noOkButton: _propTypes.default.bool,
    noTransition: _propTypes.default.bool,
    okDisabled: _propTypes.default.bool,
    okLabel: _propTypes.default.string,
    onClose: _propTypes.default.func,
    onOk: _propTypes.default.func,
    open: _propTypes.default.bool,
    square: _propTypes.default.bool,
    title: _propTypes.default.string,
    transition: _propTypes.default.element,
    transitionDuration: _propTypes.default.number,
    transitionProps: _propTypes.default.object,
    useScrollLock: _propTypes.default.bool,
    variant: _propTypes.default.oneOf(["elevation", "outlined"]),
    width: _propTypes.default.oneOf(["xs", "sm", "md", "lg", "xl", false])
  };
  static defaultProps = {
    contentDividers: false,
    contentPaddingBottom: 0.75,
    disabled: false,
    disableImplicitClose: false,
    elevation: 0,
    fullScreen: false,
    fullWidth: true,
    gap: 1,
    invertActionButtons: false,
    loading: false,
    noContent: false,
    noCloseButton: false,
    noOkButton: false,
    noFlex: false,
    noTransition: false,
    okLabel: "OK",
    open: false,
    square: false,
    useScrollLock: false,
    variant: "elevation",
    width: "sm"
  };
}
var _default = exports.default = Dialog;